import StateObserver from 'src/StateObserver';
import {
  ImageScaleViewOpts,
  TextViewOpts,
  ViewOpts,
} from '@play-co/timestep-core/ui';
import {
  ThemeProvider,
  createThemeProvider,
} from 'src/game/theme/ThemeProvider';
import {
  GachaEventTheme,
  gachaEventSchedule,
} from 'src/replicant/gacha/gacha.ruleset';
import { gachaThemeSetsubun } from './themes/gacha.theme.setsubun';
import { gachaThemeValentines } from './themes/gacha.theme.valentines';
import { gachaThemeCafe } from './themes/gacha.theme.cafe';
import { gachaThemeHanami } from './themes/gacha.theme.hanami';
import { gachaThemeChildrensDay } from './themes/gacha.theme.childrens';
import { gachaThemeMothersDay } from './themes/gacha.theme.mothersday';
import { LangBitmapOpts } from 'src/lib/ui/components/LangBitmapFontTextView';
import { gachaThemeMelonSeason } from './themes/gacha.theme.melonseason';
import { gachaThemeTanabata } from './themes/gacha.theme.tanabata';
import { gachaThemeKakigori } from './themes/gacha.theme.kakigori';
import { gachaThemeMoonviewing2025 } from './themes/gacha.theme.moonviewing2025';
import { gachaThemeMagicalBooks } from './themes/gacha.theme.magicalbooks';
import { gachaThemeTasteAutumn } from './themes/gacha.theme.tasteautumn';
import { gachaThemeWinterCandle } from './themes/gacha.theme.wintercandle';
import { gachaThemeIceQueensCastle } from './themes/gacha.theme.icequeenscastle';
import { gachaThemeChocolatier } from './themes/gacha.theme.chocolatier';
import { gachaThemeValentinesDay } from './themes/gacha.theme.valentinesday';
import { gachaThemeSpringTeaParty } from './themes/gacha.theme.springteaparty';
import { gachaThemeEasterEggHunt } from './themes/gacha.theme.easteregghunt';
import { gachaThemeGoldenWeek } from './themes/gacha.theme.goldenweek';
import { gachaThemeRainySeasonBookCafe } from './themes/gacha.theme.rainyseasonbookcafe';
import { gachaThemeTropicalIslandVacation } from './themes/gacha.theme.tropicalislandvacation';
import { gachaThemeSeashell } from './themes/gacha.theme.seashell';
import { gachaThemeEarlyAutumnPicnic } from './themes/gacha.theme.earlyautumnpicnic';
import { gachaThemePumpkinFarm } from './themes/gacha.theme.pumpkinfarm';

/** Custom now() method that will return Date.now() if replicant is not ready */
function now() {
  if (!StateObserver || !StateObserver.replicant) {
    return Date.now();
  }
  return StateObserver.now();
}

export type GachaThemeStyles = {
  gacha_popup: (args: { width: number; height: number }) => ImageScaleViewOpts;
  gacha_popup_timer: () => LangBitmapOpts;
  gacha_button_en: () => ImageScaleViewOpts;
  gacha_button_ja: () => ImageScaleViewOpts;
  timer_bg: () => ImageScaleViewOpts;
  shop_banner_en: () => ImageScaleViewOpts;
  shop_banner_ja: () => ImageScaleViewOpts;
  title_en: (args: { titleY: number; titleX: number }) => ImageScaleViewOpts;
  title_ja: (args: { titleY: number; titleX: number }) => ImageScaleViewOpts;
  intro_bg: (args: { width: number; height: number }) => ImageScaleViewOpts;
  intro_title_en: (args: {
    titleY: number;
    titleX: number;
  }) => ImageScaleViewOpts;
  intro_title_ja: (args: {
    titleY: number;
    titleX: number;
  }) => ImageScaleViewOpts;
  intro_subtitle_en: (args: { titleY: number }) => ImageScaleViewOpts;
  intro_subtitle_ja: (args: { titleY: number }) => ImageScaleViewOpts;
  intro_timer: (args: { titleY: number }) => ViewOpts;
  intro_text: (args: {
    width: number;
    height: number;
  }) => Partial<TextViewOpts>;
  intro_button: (args: { width: number; height: number }) => ViewOpts;
};

type GachaThemeStyleKey = keyof GachaThemeStyles;

type GachaThemeStyleArgs = GachaThemeStyles[GachaThemeStyleKey]['arguments'];

// Placeholder type for functions without arguments
type NoArgs = undefined;

// Modified ArgsType to return `undefined` for functions without parameters
type ArgsType<T> = T extends () => GachaThemeStyleArgs
  ? NoArgs
  : T extends (args: infer Args) => GachaThemeStyleArgs
    ? Args
    : never;

// Utility type to enforce correct argument passing based on the function's expected arguments
type EnforceArgs<K extends GachaThemeStyleKey, Args> = Args extends NoArgs
  ? []
  : [Args];

type GachaThemeProvider = {
  getStyle: <K extends GachaThemeStyleKey>(
    key: K,
    ...args: EnforceArgs<K, ArgsType<GachaThemeStyles[K]>>
  ) => ViewOpts | ImageScaleViewOpts;
  getAssets: () => string[];
  getPreloadAssets: () => string[];
  getFolder: () => string;
};

type ThemeProviderType = ThemeProvider<GachaEventTheme, GachaThemeProvider>;

/**
 * Convert raw theme to a theme provider
 * @param rawTheme
 * @returns
 */
function getThemeProvider(rawTheme: {
  styles: GachaThemeStyles;
  assets: Record<string, string>;
  preload: Record<string, string>;
  folder: string;
}): GachaThemeProvider {
  const { styles, assets, preload } = rawTheme;
  return {
    getStyle: (key, args?: GachaThemeStyleArgs) => {
      return styles[key](args || {});
    },
    getAssets: () => Object.values(assets),
    getPreloadAssets: () => Object.values(preload),
    getFolder: () => rawTheme.folder,
  };
}

/** List of themes available */
const themes = {
  setsubun: getThemeProvider(gachaThemeSetsubun),
  valentines: getThemeProvider(gachaThemeValentines),
  cafe: getThemeProvider(gachaThemeCafe),
  hanami: getThemeProvider(gachaThemeHanami),
  childrens: getThemeProvider(gachaThemeChildrensDay),
  mothersday: getThemeProvider(gachaThemeMothersDay),
  melonseason: getThemeProvider(gachaThemeMelonSeason),
  tanabata: getThemeProvider(gachaThemeTanabata),
  kakigori: getThemeProvider(gachaThemeKakigori),
  moonviewing2025: getThemeProvider(gachaThemeMoonviewing2025),
  magicalbooks: getThemeProvider(gachaThemeMagicalBooks),
  tasteautumn: getThemeProvider(gachaThemeTasteAutumn),
  wintercandle: getThemeProvider(gachaThemeWinterCandle),
  icequeenscastle: getThemeProvider(gachaThemeIceQueensCastle),
  chocolatier: getThemeProvider(gachaThemeChocolatier),
  valentinesday: getThemeProvider(gachaThemeValentinesDay),
  springteaparty: getThemeProvider(gachaThemeSpringTeaParty),
  easteregghunt: getThemeProvider(gachaThemeEasterEggHunt),
  goldenweek: getThemeProvider(gachaThemeGoldenWeek),
  rainyseasonbookcafe: getThemeProvider(gachaThemeRainySeasonBookCafe),
  tropicalislandvacation: getThemeProvider(gachaThemeTropicalIslandVacation),
  seashell: getThemeProvider(gachaThemeSeashell),
  earlyautumnpicnic: getThemeProvider(gachaThemeEarlyAutumnPicnic),
  pumpkinfarm: getThemeProvider(gachaThemePumpkinFarm),
};

/** Store providers per theme */
const providers: Partial<Record<GachaEventTheme, ThemeProviderType>> = {};

/**
 * Get the current gacha theme id, based on the current event
 * @returns
 */
export function gachaGetCurrentTheme() {
  const currentEvent = gachaEventSchedule.getCurrentEvent(now());
  return currentEvent?.metadata?.theme || 'setsubun';
}

/**
 * Retrieve the current bundle sale theme provider
 */
export function gachaTheme(): ThemeProvider<
  GachaEventTheme,
  GachaThemeProvider
> {
  const theme = gachaGetCurrentTheme();
  if (!providers[theme]) {
    providers[theme] = createThemeProvider(themes, theme);
  }
  return providers[theme]!;
}

/** Get a list of assets paths to preload */
export function gachaThemePreloadAssets() {
  const currentEvent = gachaEventSchedule.getCurrentEvent(now());
  if (!currentEvent) return [];
  return gachaTheme().getPreloadAssets();
}

/** Get all folders that should not be loaded, to avoid loading unused thems */
export function gachaThemeGetAssetsBlacklist(): string[] {
  const currentEvent = gachaEventSchedule.getCurrentEvent(now());
  // If there is no event, no gacha assets should be loaded
  if (!currentEvent) return ['assets/ui/gacha/'];

  const blacklist: string[] = [];
  for (const [id, theme] of Object.entries(themes)) {
    if (id === currentEvent.metadata?.theme) continue;
    blacklist.push(theme.getFolder());
  }
  return blacklist;
}
